"use client";

import LoginForm from "@/app/(auth)/components/LoginForm";
import VerificationCodeForm from "@/app/(auth)/components/VerificationCodeForm";
import VerifyEmailForm from "@/app/(auth)/components/VerifyEmailForm";
import { hasMultiFactorAuth } from "@/lib/api/firebase-client";
import EnrollPhoneForm from "@/app/(auth)/components/EnrollPhoneForm";
import { EmptyStateSpinner } from "@/ui/elements/EmptyState";
import {
    FirebaseAuthContextType,
    useFirebaseContext,
} from "@/app/(auth)/components/FirebaseAuthContext";
import { ReactNode, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import RegisterForm from "@/app/(auth)/components/RegisterForm";

export type RootAuthFlowProps = {
    renderInitial: (auth: FirebaseAuthContextType) => ReactNode;
};

const submitHelper = function (fn) {
    return async (values, helpers) => {
        helpers.setSubmitting(true);
        try {
            await fn(values, helpers);
        } finally {
            helpers.setSubmitting(false);
        }
    };
};

export default function RootAuthFlow({ renderInitial }: RootAuthFlowProps) {
    const qc = useQueryClient();
    useEffect(() => {
        qc.removeQueries();
    }, []);

    const auth = useFirebaseContext();

    if (auth.user == null && !auth.verify) {
        return renderInitial(auth);
    } else if (auth.user == null && auth.verify) {
        // Logging in, after verification code is sent
        return (
            <VerificationCodeForm
                onSubmit={submitHelper(auth.verifyLogin)}
                onResend={auth.resendPhoneVerification}
            />
        );
    } else if (auth.user != null && !auth.user.emailVerified) {
        // Has authenticated, but needs to verify email
        return <VerifyEmailForm onResend={auth.resendVerificationEmail} />;
    } else if (
        auth.user != null &&
        auth.user.emailVerified &&
        !hasMultiFactorAuth(auth.user)
    ) {
        // Has authenticated, but needs to enroll mfa
        if (auth.verify) {
            // Has submitted phone number, waiting for verification code
            return (
                <VerificationCodeForm
                    onSubmit={submitHelper(auth.finishEnrollPhone)}
                    onResend={auth.resendPhoneVerification}
                />
            );
        } else {
            // Needs to provide phone number
            return (
                <EnrollPhoneForm
                    onSubmit={submitHelper(auth.beginEnrollPhone)}
                />
            );
        }
    } else {
        return <EmptyStateSpinner />;
    }
}

export function RootLoginFlow() {
    return (
        <RootAuthFlow
            renderInitial={(auth) => (
                <LoginForm onSubmit={submitHelper(auth.beginLogin)} />
            )}
        />
    );
}
export function RootRegisterFlow() {
    return (
        <RootAuthFlow
            renderInitial={(auth) => (
                <RegisterForm onSubmit={submitHelper(auth.beginRegister)} />
            )}
        />
    );
}
